import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Activity, Phone, Users, TrendingUp, AlertTriangle,
  Loader2, RefreshCw, LogOut, Wallet, ChevronRight, X, Check,
  Archive, ArchiveRestore, Wrench, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  ADMIN_TOKEN_KEY, adminLogin, adminOverview, adminSystemStatus,
  adminActivationRequests, adminMarkActivation, adminClients,
  adminCreateClient, adminRecharge, adminArchiveClient, creditColor, eur,
} from "../lib/adminApi";

const DOT = { green: "#3A4D39", yellow: "#D9A441", red: "#C0392B", off: "#B9B2A6" };

export default function Plancia() {
  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY));
  const onLogin = (tok) => { localStorage.setItem(ADMIN_TOKEN_KEY, tok); setToken(tok); };
  const onLogout = () => { localStorage.removeItem(ADMIN_TOKEN_KEY); setToken(null); };
  if (!token) return <LoginView onLogin={onLogin} />;
  return <PlanciaHome token={token} onLogout={onLogout} />;
}

function LoginView({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      const res = await adminLogin(password);
      onLogin(res.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password errata o login non configurato");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <p className="text-xs tracking-[0.3em] text-brand-terra uppercase mb-2">24Vox</p>
          <h1 className="font-heading text-3xl text-brand-ink">Plancia operativa</h1>
          <p className="text-sm text-brand-inkSoft mt-2">Accesso riservato all'operatore</p>
        </div>
        <form onSubmit={submit} className="bg-white border border-brand-line rounded-2xl p-8 shadow-sm space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-brand-inkSoft block mb-2">Password admin</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="input-clean w-full px-4 py-3 rounded-lg text-base" autoFocus required />
          </div>
          <button type="submit" disabled={loading}
            className="btn-olive w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Activity className="w-5 h-5" />} Entra
          </button>
        </form>
      </div>
    </div>
  );
}

function PlanciaHome({ token, onLogout }) {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [status, setStatus] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const handle401 = (err) => {
    if (err?.response?.status === 401) { toast.error("Sessione scaduta — rientra"); onLogout(); return true; }
    return false;
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, st, rq, cl] = await Promise.all([
        adminOverview(token), adminSystemStatus(token),
        adminActivationRequests(token, true), adminClients(token, showArchived),
      ]);
      setOverview(ov); setStatus(st.services || []);
      setRequests(rq.items || []); setClients(cl.clients || []);
    } catch (err) {
      if (!handle401(err)) toast.error("Errore nel caricamento");
    } finally { setLoading(false); }
  }, [token, showArchived]); // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreateFrom = (req) => {
    setPrefill(req ? {
      company_name: req.azienda || "", website: req.sito || "", partita_iva: req.partita_iva || "",
      referente_nome: req.nome || "", referente_telefono: req.telefono || "", referente_email: req.email || "",
      _req_id: req.id,
    } : null);
    setShowCreate(true);
  };

  const quickRecharge = async (slug) => {
    const v = window.prompt(`Ricarica credito per ${slug} (€). Usa numero negativo per rettifica.`);
    if (v === null) return;
    const amount = parseFloat(String(v).replace(",", "."));
    if (isNaN(amount) || amount === 0) { toast.error("Importo non valido"); return; }
    try {
      const res = await adminRecharge(token, slug, amount, "Ricarica da plancia");
      toast.success(`Saldo ${slug}: ${eur(res.balance_eur)}`);
      loadAll();
    } catch (err) { if (!handle401(err)) toast.error("Ricarica fallita"); }
  };

  const quickRestore = async (slug) => {
    try {
      await adminArchiveClient(token, slug, false);
      toast.success("Cliente ripristinato");
      loadAll();
    } catch (err) { if (!handle401(err)) toast.error("Ripristino fallito"); }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs tracking-[0.3em] text-brand-terra uppercase">24Vox</p>
            <h1 className="font-heading text-2xl md:text-3xl text-brand-ink">Plancia operativa</h1>
            <p className="text-sm text-brand-inkSoft">Tutto sotto controllo, da un posto solo.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} className="p-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2" title="Aggiorna">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={() => openCreateFrom(null)}
              className="btn-olive px-4 py-2 rounded-lg font-semibold flex items-center gap-2">
              <Plus className="w-4 h-4" /> Nuovo cliente
            </button>
            <button onClick={() => navigate("/plancia/visite")} className="p-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2" title="Visite al sito">
              <BarChart3 className="w-4 h-4" />
            </button>
            <button onClick={() => navigate("/admin/retell")} className="p-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2" title="Strumenti tecnici (Retell)">
              <Wrench className="w-4 h-4" />
            </button>
            <button onClick={onLogout} className="p-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2" title="Esci">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Striscia stato sistema */}
        <div className="bg-white border border-brand-line rounded-2xl px-4 py-3 mb-5 flex flex-wrap items-center gap-x-5 gap-y-2">
          {status.length === 0 && <span className="text-sm text-brand-inkSoft">Stato servizi…</span>}
          {status.map((s) => (
            <span key={s.service} className="flex items-center gap-2 text-sm">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: s.ok ? DOT.green : DOT.red }} />
              <span className="font-medium">{s.service}</span>
              {s.balance_eur != null && <span className="text-brand-inkSoft">({eur(s.balance_eur)})</span>}
            </span>
          ))}
        </div>

        {/* 4 card numeri vitali */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <VitalCard icon={<Users className="w-5 h-5" />} label="Clienti attivi" value={overview?.clienti_attivi ?? "—"} />
          <VitalCard icon={<Phone className="w-5 h-5" />} label="Chiamate oggi" value={overview?.chiamate_oggi ?? "—"} />
          <VitalCard icon={<TrendingUp className="w-5 h-5" />} label="Ricavo / anno" value={overview ? eur(overview.ricavo_anno_eur) : "—"} />
          <VitalCard icon={<AlertTriangle className="w-5 h-5" />} label="Crediti a rischio"
            value={overview?.crediti_a_rischio ?? "—"} danger={(overview?.crediti_a_rischio ?? 0) > 0} />
        </div>

        {/* Coda attivazioni */}
        {requests.length > 0 && (
          <div className="mb-6">
            <h2 className="font-heading text-lg text-brand-ink mb-3">Coda attivazioni <span className="text-brand-terra">({requests.length})</span></h2>
            <div className="grid md:grid-cols-2 gap-3">
              {requests.map((r) => (
                <div key={r.id} className="bg-white border border-brand-line rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-bg2 flex items-center justify-center text-brand-olive font-semibold shrink-0">
                    {initials(r.nome || r.azienda)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{r.nome || "—"} · <span className="text-brand-inkSoft">{r.azienda || "—"}</span></div>
                    <div className="text-xs text-brand-inkSoft truncate">{r.sito || "—"} · {timeAgo(r.created_at)}</div>
                  </div>
                  <button onClick={() => openCreateFrom(r)} className="btn-olive px-3 py-2 rounded-lg text-sm font-semibold shrink-0">
                    Crea account
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabella clienti */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg text-brand-ink">Clienti</h2>
          <button onClick={() => setShowArchived((v) => !v)}
            className="text-xs px-3 py-1.5 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2 flex items-center gap-2 text-brand-inkSoft">
            <Archive className="w-3.5 h-3.5" />
            {showArchived ? "Nascondi archiviati" : "Mostra archiviati"}
          </button>
        </div>
        <div className="bg-white border border-brand-line rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand-bg2 text-brand-inkSoft text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3">Azienda</th>
                  <th className="text-left px-4 py-3">Credito</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Chiamate mese</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading && clients.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-brand-inkSoft">
                    <Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                )}
                {!loading && clients.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-brand-inkSoft">Nessun cliente ancora.</td></tr>
                )}
                {clients.map((c) => {
                  const col = creditColor(c.balance_eur);
                  return (
                    <tr key={c.slug} className={`border-t border-brand-line hover:bg-brand-bg2 cursor-pointer ${c.archiviato ? "opacity-50" : ""}`}
                      onClick={() => navigate(`/plancia/cliente/${c.slug}`)}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-brand-ink flex items-center gap-2">
                          {c.company_name || c.slug}
                          {c.archiviato && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EDE7DD] text-brand-inkSoft font-medium uppercase tracking-wide">archiviato</span>}
                        </div>
                        <div className="text-xs text-brand-inkSoft">{c.numero || "nessun numero"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: DOT[col] }} />
                          {eur(c.balance_eur)}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">{c.chiamate_mese ?? 0}</td>
                      <td className="px-4 py-3"><StatePill sospeso={c.sospeso} col={col} /></td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          {c.archiviato && (
                            <button onClick={() => quickRestore(c.slug)} title="Ripristina"
                              className="p-2 rounded-lg border border-brand-line hover:bg-white text-brand-olive"><ArchiveRestore className="w-4 h-4" /></button>
                          )}
                          <button onClick={() => quickRecharge(c.slug)} title="Ricarica"
                            className="p-2 rounded-lg border border-brand-line hover:bg-white"><Wallet className="w-4 h-4" /></button>
                          <button onClick={() => navigate(`/plancia/cliente/${c.slug}`)} title="Dettaglio"
                            className="p-2 rounded-lg border border-brand-line hover:bg-white"><ChevronRight className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateClientModal token={token} prefill={prefill}
          onClose={() => setShowCreate(false)}
          onCreated={async (reqId) => {
            setShowCreate(false);
            if (reqId) { try { await adminMarkActivation(token, reqId); } catch (e) {} }
            loadAll();
          }} />
      )}
    </div>
  );
}

function VitalCard({ icon, label, value, danger }) {
  return (
    <div className="bg-white border border-brand-line rounded-2xl p-4">
      <div className="flex items-center gap-2 text-brand-inkSoft mb-2">{icon}<span className="text-xs uppercase tracking-wider">{label}</span></div>
      <div className={`font-heading text-2xl ${danger ? "text-[#C0392B]" : "text-brand-ink"}`}>{value}</div>
    </div>
  );
}

function StatePill({ sospeso, col }) {
  if (sospeso) return <span className="text-xs px-2 py-1 rounded-full bg-[#FBEAEA] text-[#C0392B] font-medium">bloccato</span>;
  if (col === "yellow") return <span className="text-xs px-2 py-1 rounded-full bg-[#FBF3E0] text-[#9A6E14] font-medium">sotto soglia</span>;
  if (col === "red") return <span className="text-xs px-2 py-1 rounded-full bg-[#FBEAEA] text-[#C0392B] font-medium">credito negativo</span>;
  return <span className="text-xs px-2 py-1 rounded-full bg-[#EAF0E8] text-brand-olive font-medium">attivo</span>;
}

function CreateClientModal({ token, prefill, onClose, onCreated }) {
  const [f, setF] = useState({
    company_name: prefill?.company_name || "", website: prefill?.website || "", partita_iva: prefill?.partita_iva || "",
    referente_nome: prefill?.referente_nome || "", referente_telefono: prefill?.referente_telefono || "", referente_email: prefill?.referente_email || "",
    default_gender: "F", number: "", bonifico: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name.trim()) { toast.error("La ragione sociale è obbligatoria"); return; }
    setLoading(true);
    try {
      const payload = {
        company_name: f.company_name.trim(), website: f.website.trim(), partita_iva: f.partita_iva.trim(),
        referente_nome: f.referente_nome.trim(), referente_telefono: f.referente_telefono.trim(), referente_email: f.referente_email.trim(),
        default_gender: f.default_gender,
      };
      if (f.number.trim()) payload.number = f.number.trim();
      const res = await adminCreateClient(token, payload);
      // ricarica da bonifico (oltre ai 10€ di benvenuto)
      const bon = parseFloat(String(f.bonifico).replace(",", "."));
      if (!isNaN(bon) && bon > 0) {
        try { await adminRecharge(token, res.slug, bon, "Ricarica da bonifico (creazione)"); } catch (e2) {}
      }
      setResult(res);
      toast.success("Cliente creato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Creazione fallita");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start md:items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-brand-bg border border-brand-line rounded-2xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-line">
          <h3 className="font-heading text-xl">Nuovo cliente 24Vox</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-brand-bg2"><X className="w-5 h-5" /></button>
        </div>

        {result ? (
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-brand-olive"><Check className="w-5 h-5" /> <span className="font-semibold">Account creato</span></div>
            <Cred label="Slug" value={result.slug} />
            <Cred label="Password dashboard" value={result.dashboard_password} />
            <Cred label="Link dashboard" value={`${window.location.origin}/dashboard/${result.slug}`} />
            {result.number && <Cred label="Numero" value={result.number} />}
            {result.telegram_link && <Cred label="Link Telegram" value={result.telegram_link} />}
            {result.telegram_qr && (
              <div className="bg-white border border-brand-line rounded-lg p-3 flex flex-col items-center gap-2">
                <p className="text-xs text-brand-inkSoft self-start">QR Telegram — il cliente lo scansiona per collegare il suo Telegram al servizio</p>
                <img src={`data:image/png;base64,${result.telegram_qr}`} alt="QR Telegram" className="w-44 h-44" />
              </div>
            )}
            <p className="text-xs text-brand-inkSoft">Credito di benvenuto 10€ già accreditato. Copia e invia al cliente: link dashboard + password + QR Telegram.</p>
            <button onClick={() => onCreated(prefill?._req_id)} className="btn-olive w-full py-3 rounded-lg font-semibold">Fatto</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <Section title="Dati azienda (per fattura)">
              <Field label="Ragione sociale *" value={f.company_name} onChange={set("company_name")} required />
              <Field label="Sito web" value={f.website} onChange={set("website")} placeholder="azienda.it" />
              <Field label="P.IVA" value={f.partita_iva} onChange={set("partita_iva")} />
            </Section>
            <Section title="Referente">
              <Field label="Nome" value={f.referente_nome} onChange={set("referente_nome")} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Telefono" value={f.referente_telefono} onChange={set("referente_telefono")} />
                <Field label="Email" value={f.referente_email} onChange={set("referente_email")} />
              </div>
            </Section>
            <Section title="Voce">
              <div className="flex gap-2">
                {[["F", "Anna (donna)"], ["M", "Alberto (uomo)"]].map(([v, lbl]) => (
                  <button type="button" key={v} onClick={() => setF((s) => ({ ...s, default_gender: v }))}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium ${f.default_gender === v ? "border-brand-terra bg-[#FBEEE8] text-brand-terra" : "border-brand-line bg-white text-brand-inkSoft"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </Section>
            <Section title="Numero 24Vox">
              <Field label="Numero da assegnare (E.164)" value={f.number} onChange={set("number")} placeholder="+39…" />
              <p className="text-xs text-brand-inkSoft">Acquisto numero italiano: in arrivo (Fase D). Per ora assegna un numero già disponibile.</p>
            </Section>
            <Section title="Credito">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Benvenuto (auto)" value="10,00 €" disabled />
                <Field label="Ricarica da bonifico (€)" value={f.bonifico} onChange={set("bonifico")} placeholder="0" />
              </div>
            </Section>
            <p className="text-xs text-brand-inkSoft">Alla creazione genero: slug, password e link dashboard.</p>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-lg border border-brand-line bg-white font-semibold">Annulla</button>
              <button type="submit" disabled={loading} className="btn-olive flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Crea account
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-brand-terra mb-2">{title}</p>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs text-brand-inkSoft block mb-1">{label}</label>
      <input className="input-clean w-full px-3 py-2 rounded-lg text-sm disabled:opacity-60" {...props} />
    </div>
  );
}
function Cred({ label, value }) {
  return (
    <div className="bg-white border border-brand-line rounded-lg px-3 py-2 flex items-center justify-between gap-2">
      <div className="min-w-0"><div className="text-xs text-brand-inkSoft">{label}</div><div className="font-mono text-sm truncate">{value}</div></div>
      <button onClick={() => { navigator.clipboard?.writeText(String(value)); toast.success("Copiato"); }}
        className="text-xs px-2 py-1 rounded border border-brand-line hover:bg-brand-bg2 shrink-0">copia</button>
    </div>
  );
}

function initials(s) {
  const parts = String(s || "?").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}
function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `da ${Math.max(1, Math.floor(diff / 60))} min`;
  if (diff < 86400) return `da ${Math.floor(diff / 3600)} h`;
  return `da ${Math.floor(diff / 86400)} g`;
}
